/**
 * DebugModeBanner — Persistent top banner shown while DEBUG mode is ON.
 *
 * - Reminds the admin that sensitive actions are exposed
 * - Shows remaining TTL (auto-expires after 1h)
 * - Quick "Désactiver" button + "Paramètres Développeur" link
 *
 * Rendered at the top of AdminLayout — appears on every admin page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, X } from 'lucide-react';
import useDebugMode from '../hooks/useDebugMode';

const formatRemaining = (ms) => {
  if (!ms || ms <= 0) return '—';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  const s = Math.max(1, Math.floor((ms % 60000) / 1000));
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const DebugModeBanner = () => {
  const { debugMode, setDebugMode, expiresInMs } = useDebugMode();
  if (!debugMode) return null;

  return (
    <div
      data-testid="debug-mode-banner"
      className="sticky top-0 z-40 w-full border-b border-amber-500/30 bg-amber-500/10 backdrop-blur"
    >
      <div className="flex items-center gap-3 px-4 py-2 text-[11px] text-amber-200">
        <Wrench className="w-3.5 h-3.5 text-amber-400 shrink-0" />
        <span className="font-semibold text-amber-300">Mode DEBUG activé</span>
        <span className="text-amber-200/70 hidden sm:inline">
          — actions de maintenance exposées. Expire dans
        </span>
        <span
          data-testid="debug-mode-ttl"
          className="font-mono text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded"
          title="Le mode s'auto-désactive après 1h"
        >
          {formatRemaining(expiresInMs)}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/admin/settings?tab=developer"
            className="text-amber-200/80 hover:text-amber-100 underline underline-offset-2"
            data-testid="debug-mode-banner-settings-link"
          >
            Paramètres
          </Link>
          <button
            type="button"
            onClick={() => setDebugMode(false)}
            className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100"
            data-testid="debug-mode-banner-disable"
            title="Désactiver le mode DEBUG"
          >
            <X className="w-3 h-3" /> Désactiver
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugModeBanner;
